import { Module } from 'vuex';
import { createSimpleMutations } from '@/helpers/vuex';
import { RootState } from '@/store';
import { getPayments } from '@/services/PaymentsService';
import { CompanyDepartment, CompanyContract } from '@/services/CompanyService';
import { ROUTE_PARAMS_TYPE } from '@/router';
import { CompanyUserAccess, Payments, ReportType } from '@/gql/graphql';
import * as actions from './actions';

export interface CabinetState {
  user: AuthUser | null;
  company: Company | null;
  companies: Company[];
  departments: CompanyDepartment[];
  fuel: Fuel[];
  fuelGroups: FuelGroup[];
  regions: Region[];
  payments: Payments;
  isRequestInProgress: boolean;
  errorMessage: string;
  department: CompanyDepartment | null;
}

const cabinet: Module<CabinetState, RootState> = {
  namespaced: true,

  state: (): CabinetState => ({
    user: null,
    company: null,
    companies: [],
    departments: [],
    fuel: [],
    fuelGroups: [],
    regions: [],
    isRequestInProgress: false,
    errorMessage: '',
    payments: {} as Payments,
    department: null,
  }),

  getters: {
    isLoaded: (state: CabinetState) => state.user !== null,
    isDepartment: (state) => Boolean(state.department?.id),
    user: (state: CabinetState) => state.user,
    company: (state: CabinetState) => state.company,
    companies: (state: CabinetState) => state.companies,
    fuel: ({ fuel }) => fuel || [],
    fuelGroups: ({ fuelGroups }) => fuelGroups || [],
    regions: (state: CabinetState) => state.regions || [],
    needToFeelData: ({ company }: CabinetState): boolean => {
      if (!company) {
        return false;
      }
      const kppOk = company?.companyType?.toUpperCase() === 'ИП' || !!company?.kpp;
      return !(
        kppOk &&
        company.inn &&
        company.ogrn &&
        company.officialAddress &&
        company.contract?.account &&
        company.contract?.bik &&
        company.contract.correspondence
      );
    },

    companyPartnersIds({ companies }) {
      let result: string[] = [];

      if (companies) {
        companies.forEach((company) => {
          if (company.partners) {
            const partnersIds = company.partners.map((p) => p.id);
            result = result.concat(partnersIds);
          }
        });
      }

      return result;
    },

    companiesTypesById({ companies }) {
      const result: { [key: string]: string } = {};

      if (companies) {
        companies.forEach((company) => {
          result[company.id] = company.type;

          if (company.contracts) {
            company.contracts.forEach((contract) => {
              if (contract.id) {
                result[contract.id] = company.type;
              }
            });
          }
        });
      }

      return result;
    },

    companiesContractsIds({ companies }) {
      let result: string[] = [];

      if (companies) {
        companies.forEach((company) => {
          if (company.contracts) {
            const ids = company.contracts.map((c) => c.id).filter(Boolean) as string[];
            result = result.concat(ids);
          }
        });
      }

      return result;
    },

    getCompanyRouteTypeById({ company }, { companyPartnersIds, companiesTypesById, companiesContractsIds }) {
      return (id: string | undefined) => {
        const isInDepartment = (id && companyPartnersIds.includes(id)) || Boolean(company?.partner);
        const companyType = id && companiesTypesById[id];

        if (isInDepartment && !companyType) {
          return ROUTE_PARAMS_TYPE.department;
        }

        if (companyType === 'azs') {
          return ROUTE_PARAMS_TYPE.station;
        }

        if (id && companiesContractsIds.includes(id)) {
          return ROUTE_PARAMS_TYPE.contract;
        }

        return ROUTE_PARAMS_TYPE.company;
      };
    },

    // хлебные крошки есть только у корп. клиента
    breadcrumbs({ company, companies }): { title: string; urlPath: string }[] {
      let rootCompanyType: string;
      let rootCompanyId: string;

      if (!company) {
        return [];
      }

      if (company.type === 'tk') {
        const companyContracts = companies.find((c) => c.id === company.id)?.contracts;
        // если у компании несколько контрактов, то нужно выбрать текущий
        if (companyContracts && companyContracts.length > 1 && company.contract?.id) {
          rootCompanyType = 'contract';
          rootCompanyId = company.contract?.id;
        } else {
          rootCompanyType = 'company';
          rootCompanyId = company.id;
        }
      } else {
        rootCompanyType = 'company';
        rootCompanyId = company.id;
      }

      const result = [
        {
          title: company.name || 'Компания',
          urlPath: `/${rootCompanyType}/${rootCompanyId}/orders`,
        },
      ];

      const hasDepartment = company.partner;
      const hasSubDepartment = company.partner?.parent;
      if (hasSubDepartment) {
        result.push({
          title: company.partner?.parent?.name || '',
          urlPath: hasDepartment ? `/department/${company.partner?.parent?.id}/orders` : '',
        });
      }

      if (hasDepartment) {
        result.push({
          title: company.partner?.name || '',
          urlPath: '',
        });
      }

      return result;
    },
  },

  mutations: {
    ...createSimpleMutations<CabinetState>([
      'companies',
      'payments',
      'isRequestInProgress',
      'errorMessage',
      'departments',
      'department',
    ]),

    setUser: (state: CabinetState, user: AuthUser | null) => {
      state.user = <AuthUser>user;
    },
    setCompany: (state: CabinetState, company: Company | null) => {
      state.company = <Company>company;
      const companyInList = state.user?.companies?.find(({ id }) => id === company?.id);
      if (companyInList) {
        companyInList.name = company?.name || '';
      }
    },
    updateCompany: (state: CabinetState, company: Company | null) => {
      if (!company) {
        return;
      }
      state.company = { ...state.company, ...company };
    },
    updateInfo: (state: CabinetState, { fuel, fuelGroups, regions }) => {
      state.fuel = fuel;
      state.regions = regions;
      state.fuelGroups = fuelGroups;
    },

    companyReportType: (state, value: ReportType) => {
      if (state.company) {
        state.company.reportType = value;
      }
    },
  },

  actions: {
    ...actions,

    async fetchPayments(
      { commit },
      {
        companyId,
        contractId,
        partnerId,
        page,
        pageSize,
      }: { partnerId: string; contractId: string; companyId: string; page: number; pageSize: number },
    ) {
      commit('isRequestInProgress', true);
      try {
        const response = await getPayments({ companyId, contractId, partnerId, filter: { page, size: pageSize } });
        const payments = response.cabinet?.company?.payments;
        if (!payments) {
          throw new Error('Не удалось получить список платежей');
        }
        commit('updateCompany', { payments });
        commit('payments', payments);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Не удалось получить список платежей';
        commit('errorMessage', errorMessage);
      } finally {
        commit('isRequestInProgress', false);
      }
    },
  },
};

export default cabinet;

export type Partner = {
  id: string;
  name: string;
  driversCount: number;
  registeredDriversCount: number;
};

export type CompanyContact = {
  name?: string;
  phone?: string;
  email?: string;
};

export type Company = {
  id: string;
  name: string;
  type: string;
  role: string;
  b2b: boolean;
  payments?: {
    balance: number;
    needMoney: boolean;
  };
  hasDrivers?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  drivers?: any;

  brand?: string;
  companyType?: string;
  officialAddress?: string;
  postAddress?: string;
  inn?: string;
  kpp?: string;
  ogrn?: string;
  contact?: string;
  director?: string;
  phone?: string;
  email?: string;
  status?: string;
  source?: string;
  stat?: {
    departmentsCount: number;
    allDepartmentsCount: number;
    driversCount: number;
    activeDriversCount: number;
  };
  buh?: CompanyContact;
  tech?: CompanyContact;
  additional?: { commentary: string };
  contract?: CompanyContract;
  contracts?: CompanyContract[];
  reportType?: ReportType;
  partner?: CompanyDepartment;
  partners?: {
    id: string;
    name: string;
  }[];
  registered: string;
  cars: {
    count: number;
    items: unknown[];
  };
  userAccess: CompanyUserAccess[];
  simple?: boolean;
};

export type AuthUser = {
  email?: string;
  phone?: string;
  companies: Company[];
};

export type Fuels = {
  fuel: Fuel[];
  fuelGroups: FuelGroup[];
  regions: Region[];
};

export type Fuel = {
  id: string;
  name: string;
  shortName?: string;
  group?: {
    id: string;
    name?: string;
  } | null;
  groupId?: string;
  description?: string | null;
};

export type FuelGroup = {
  id: string;
  name: string;
};

type Region = {
  id: string;
  name: string;
};
